import style from './style.module.scss'
import classnames from 'classnames'
import { useState, useEffect } from 'react'
import { Base, Icon } from '@crossfit/ui'
import { useMediaQuery } from 'hooks/useMediaQuery'
import logo from './atk-logo.svg'
import { Text } from '@crossfit/design-system'
import { Link } from 'react-router-dom'
import { ReactComponent as ExternalLink } from 'components/Admin/images/external-link.svg'
import Select, { StylesConfig } from 'react-select'
import { Role } from 'types'
import { useAffiliate } from 'hooks/useAffiliate'

type MainNavigationProps = {
  children: React.ReactNode
  userMenu: React.ReactNode
}

type ResourcesMenuProps = {
  children: React.ReactNode
}

export function MainNavigation({ children, userMenu }: MainNavigationProps) {
  const { myAffiliates, selectedAffiliate, setSelectedAffiliate } =
    useAffiliate()
  const [isMenuExpanded, setIsMenuExpanded] = useState(false)
  const [isUserMenuExpanded, setIsUserMenuExpanded] = useState(false)
  const [mobileSelectOpen, setMobileSelectOpen] = useState(false)
  const isMobile = useMediaQuery(`(max-width: 1183px)`)
  const affiliateSelectorOptions = myAffiliates?.map((affiliate) => {
    return {
      value: affiliate.affiliateId,
      label: affiliate.affiliateName,
      customAbbreviation: affiliate.roles
        .filter((r) => [Role.LOR, Role.Coach, Role.Manager].includes(r as Role))
        .map((r) => (r === Role.LOR ? 'Owner' : r))[0],
    }
  })
  const affiliateSelectorStyles: StylesConfig = {
    control: (styles) => ({
      ...styles,
      backgroundColor: '#1a1a1a',
      borderColor: '#333',
    }),
    option: ({ isSelected }) => {
      return {
        backgroundColor: isSelected ? '#333' : '#1A1A1A',
        minHeight: '60px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }
    },
    menuList: () => {
      return {
        padding: '0px',
      }
    },
  }

  const formatOptionLabel = (
    { value, label, customAbbreviation },
    { context },
  ) => {
    if (context === 'value') {
      return (
        <div className={style.affiliateSelectorContainerSelected}>
          <Text
            type={'body'}
            size={'sm'}
            className={style.affiliateSelectorOptionNameText}
          >
            {label}
          </Text>
        </div>
      )
    }
    return (
      <div className={style.affiliateSelectorContainer}>
        <div className={style.affiliateSelectorTextContainer}>
          <Text
            type={'overline'}
            size={'xs'}
            className={style.affiliateSelectorOptionRoleText}
          >
            {customAbbreviation}
          </Text>
          <Text
            type={'body'}
            size={'sm'}
            bold
            className={style.affiliateSelectorOptionNameText}
          >
            {label}
          </Text>
        </div>
        {selectedAffiliate.affiliateId === value && (
          <Icon
            name={'check'}
            fontSize={12}
            className={style.affiliateSelectorCheck}
          />
        )}
      </div>
    )
  }

  useEffect(() => {
    if (isUserMenuExpanded) {
      setIsMenuExpanded(false)
    }
  }, [isUserMenuExpanded])

  useEffect(() => {
    if (isMenuExpanded) {
      setIsUserMenuExpanded(false)
    }
  }, [isMenuExpanded])

  return (
    <header className={style.headerWrapper}>
      <div className={style.top}>
        <div className={style.inner}>
          {isMobile ? (
            <div className={style.logo}>
              <img src={logo} alt='CrossFit' width='70' height='32' />
            </div>
          ) : (
            <Link className={style.backTo} to='https://www.crossfit.com'>
              <Text
                as='div'
                type='overline'
                size='sm'
                dangerousHTML={`Back to <span>crossfit.com</span>`}
              />
              <ExternalLink className={style.externalLinkIcon} />
            </Link>
          )}
          <div className={style.icons}>
            {myAffiliates && myAffiliates.length > 1 && (
              <div
                className={style.affiliateSelectorContainer}
                onClick={() => {
                  if (!mobileSelectOpen) {
                    document.body.style.overflow = 'hidden'
                  } else {
                    document.body.style.overflow = 'scroll'
                  }

                  setMobileSelectOpen(!mobileSelectOpen)
                }}
              >
                {isMobile ? (
                  <>
                    <div className={style.mobileAffiliateSelector}>
                      <Text type='body' size='sm'>
                        {selectedAffiliate?.affiliateName}
                      </Text>
                      <Icon
                        name='chevron'
                        className={classnames(
                          style.chevron,
                          mobileSelectOpen && style.chevronInverted,
                        )}
                      />
                    </div>
                  </>
                ) : (
                  <Select
                    options={affiliateSelectorOptions}
                    styles={affiliateSelectorStyles}
                    formatOptionLabel={formatOptionLabel}
                    onChange={(currentValue: {
                      value: any
                      label: string
                      customAbbreviation: string
                    }) => {
                      setSelectedAffiliate(currentValue.value)
                    }}
                    value={affiliateSelectorOptions.find(
                      (o) => o.value === selectedAffiliate.affiliateId,
                    )}
                    components={{ IndicatorSeparator: () => null }}
                  />
                )}
              </div>
            )}
            {userMenu && (
              <nav className={classnames(style.menu, style.userMenu)}>
                <button
                  className={style.button}
                  onClick={() => setIsUserMenuExpanded((s) => !s)}
                  aria-expanded={isUserMenuExpanded ? 'true' : 'false'}
                >
                  <Icon
                    name={isUserMenuExpanded ? 'close' : 'user'}
                    className={style.icon}
                  />
                </button>
                <div
                  className={classnames(
                    style.dropdownMenu,
                    isUserMenuExpanded && style.isExpanded,
                  )}
                  onClick={() => setIsUserMenuExpanded((s) => !s)}
                >
                  {userMenu}
                </div>
              </nav>
            )}
            {isMobile && children && (
              <nav className={classnames(style.menu, style.mainMenu)}>
                <button
                  className={style.button}
                  onClick={() => setIsMenuExpanded((s) => !s)}
                  aria-expanded={isMenuExpanded ? 'true' : 'false'}
                >
                  <Icon
                    name={isMenuExpanded ? 'close' : 'hamburger'}
                    className={style.icon}
                  />
                </button>
                <div
                  className={classnames(
                    style.dropdownMenu,
                    isMenuExpanded && style.isExpanded,
                  )}
                  onClick={() => setIsMenuExpanded((s) => !s)}
                >
                  {children}
                </div>
              </nav>
            )}
          </div>
        </div>
      </div>
      {!isMobile && (
        <div className={style.bottom}>
          <div className={style.mainInner}>
            <div className={style.logo}>
              <img src={logo} alt='CrossFit' width='117' height='47' />
            </div>
            <nav className={classnames(style.mainMenu, style.nav)}>
              {children}
            </nav>
          </div>
        </div>
      )}

      {isMobile && mobileSelectOpen && (
        <div
          className={style.mobileAffiliateMenu}
          onClick={() => setMobileSelectOpen(false)}
        >
          {myAffiliates?.map((affiliate) => (
            <div
              key={affiliate?.affiliateId}
              className={classnames(
                style.mobileAffiliateSelectorOption,
                selectedAffiliate?.affiliateName === affiliate?.affiliateName &&
                  style.mobileAffiliateSelectorOptionSelected,
              )}
              onClick={() => setSelectedAffiliate(affiliate?.affiliateId)}
            >
              <div className={style.mobileAffiliateSelectorOptionLeft}>
                <Text
                  type='overline'
                  size='xs'
                  className={style.mobileAffiliateSelectorOptionRole}
                >
                  {
                    affiliate.roles
                      .filter((r) =>
                        [Role.LOR, Role.Coach, Role.Manager].includes(
                          r as Role,
                        ),
                      )
                      .map((r) => (r === Role.LOR ? 'Owner' : r))[0]
                  }
                </Text>
                <Text
                  type='body'
                  size='sm'
                  bold
                  className={style.mobileAffiliateSelectorOptionName}
                >
                  {affiliate?.affiliateName}
                </Text>
              </div>
              {selectedAffiliate?.affiliateName ===
                affiliate?.affiliateName && (
                <Icon name='check' className={style.check} />
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  )
}

export function ResourcesMenu({ children }: ResourcesMenuProps) {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false)
  const [isResourcesMenuExpanded, setIsResourcesMenuExpanded] = useState(false)
  const isMobile = useMediaQuery(`(max-width: 1183px)`)

  useEffect(() => {
    if (isResourcesMenuExpanded) {
      setIsMenuExpanded(false)
    }
  }, [isResourcesMenuExpanded])

  useEffect(() => {
    if (isMenuExpanded) {
      setIsResourcesMenuExpanded(false)
    }
  }, [isMenuExpanded])

  return (
    <div
      className={classnames(style.link, style.hasSubmenu)}
      onClick={() => setIsMenuExpanded((s) => !s)}
      aria-expanded={isMenuExpanded ? 'true' : 'false'}
    >
      <div className={classnames(style.navLink, style.sectionHasSubmenu)}>
        Business Resources
      </div>
      <div
        className={classnames(
          style.dropdownMenu,
          isResourcesMenuExpanded && style.isExpanded,
        )}
        onClick={() => isMobile && setIsResourcesMenuExpanded((s) => !s)}
      >
        {children}
      </div>
    </div>
  )
}

type MainNavigationLinkProps = React.ComponentProps<typeof Base>

export function MainNavigationLink({
  className,
  as = 'a',
  onClick,
  ...props
}: MainNavigationLinkProps) {
  return (
    <div className={classnames(style.link, style[className])} onClick={onClick}>
      <Base as={as} className={style.navLink} {...props} />
    </div>
  )
}
